import React, { useContext, useEffect, useMemo, useState } from "react";
import { Typography, Select, Radio, Checkbox, Row, Col } from "antd";
import { SbButton, SbSelect, SbNotification, SbSpin } from "uiCore";
import { AuthContext } from "State/AuthContext";
import { PageHeaderStyled, HeaderLeftStyled, HeadingRowStyled } from "styles/common.styles";
import { PageTransitionStyled } from "./talentMobility.animations";
import { CardStyled, FieldBlockStyled, RadioRowStyled, CurrentRoleRowStyled } from "./AspirationCaptureV2.styles";
import { getOrgLocationNames } from "./orgLocations.utils";
import useOrgPlacement, { ORG_PLACEMENT_FIELDS, placementMatchesJob } from "./useOrgPlacement";
import useAuthStore from "Store/useAuthStore";
import { decodeToken } from "Components/SbSessionCheckerComponent/SbSessionChecker";
import {
  getTalentMobilityPublishedJobs,
  getTalentMobilityPeopleProfileDetails,
} from "Api/apiService";

const { Title, Text } = Typography;

// Every field it labels blocks Save & Continue until filled (see canSave) —
// the mark just makes that visible. Business Unit/Function/Sub-function are
// optional (just a Target Role filter) so they use a plain label instead.
const RequiredLabel = ({ children }) => (
  <Text strong>
    <Text type="danger">* </Text>
    {children}
  </Text>
);

// req #3 — captured on first visit to Talent Mobility (Self) (when
// POST /career-paths finds no saved aspiration for the caller), editable
// afterward from the Career Pathways summary bar. Locations come from real
// org metadata — not a hardcoded list — via getOrgLocationNames
// (orgLocations.utils.js), deduped across legal entities.
//
// Pure capture form: initialAspiration (the parent's hydrated/last-saved
// values, or null on first visit) seeds the fields, and Save & Continue just
// hands the captured values to onSaved — the parent
// (TalentMobilityEmployeeV2) owns POST /career-paths and all pathway state.
const AspirationCaptureV2 = ({ initialAspiration, onSaved }) => {
  const { orgMetaData } = useContext(AuthContext);

  const [targetRoleId, setTargetRoleId] = useState(initialAspiration?.targetRoleId ?? null);
  const [mobile, setMobile] = useState(initialAspiration?.mobile ?? null);
  const [locations, setLocations] = useState(initialAspiration?.locations || []);
  const [publishedJobs, setPublishedJobs] = useState(null); // null = still loading

  // Business Unit / Function / Sub-function cascade above Target Role.
  const placementCascade = useOrgPlacement(orgMetaData, initialAspiration);

  // Target Role must carry a real JD sb_unique_id — POST /career-paths
  // rejects anything not in job_desc — so the dropdown is filled ONLY from
  // GET /published-jobs (no mock fallback), with a spinner while it loads.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const jobs = await getTalentMobilityPublishedJobs();
        if (!cancelled) setPublishedJobs(Array.isArray(jobs) ? jobs : []);
      } catch (error) {
        if (cancelled) return;
        setPublishedJobs([]);
        SbNotification.error(
          "Unable to load target roles",
          error?.response?.data?.message || "Please refresh and try again."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // While /published-jobs is still loading, antd renders a selected value
  // with no matching option as the raw id (e.g. re-opening the form via
  // Edit) — seed the stored selection's title as a placeholder option until
  // the real list arrives. Once it has, the list narrows to jobs matching
  // the org placement above, and duplicate postings sharing a title
  // collapse into ONE option (an already-selected posting stays its
  // title's representative, so a stored pick survives the dedupe).
  const roleOptions = useMemo(() => {
    if (publishedJobs === null) {
      return targetRoleId && initialAspiration?.targetRoleTitle
        ? [{ value: targetRoleId, label: initialAspiration.targetRoleTitle }]
        : [];
    }
    const idByTitle = new Map();
    publishedJobs
      .filter((job) => placementMatchesJob(placementCascade.placement, job))
      .forEach((job) => {
        const title = job.title || job.sb_unique_id;
        if (!idByTitle.has(title) || job.sb_unique_id === targetRoleId) {
          idByTitle.set(title, job.sb_unique_id);
        }
      });
    return [...idByTitle].map(([title, id]) => ({ value: id, label: title }));
  }, [publishedJobs, targetRoleId, initialAspiration, placementCascade.placement]);

  // A selection that drops out of the offered list — its JD unpublished, or
  // the org placement above narrowed past it — would render as its raw id
  // and 400 on save, so force a fresh pick instead.
  useEffect(() => {
    if (publishedJobs === null) return;
    setTargetRoleId((prev) =>
      prev && !roleOptions.some((option) => option.value === prev) ? null : prev
    );
  }, [publishedJobs, roleOptions]);

  // Current Role comes from POST /people-profile-details for the signed-in
  // user (currentRole.roleName — the ACTIVE employee_jd_assignment's JD).
  // Display-only: /career-paths re-resolves the current role server-side
  // from the JWT, so this is never sent in the save payload.
  const accessToken = useAuthStore((state) => state.accessToken);
  const currentUserId = useMemo(() => {
    const claims = decodeToken(accessToken);
    return claims?.sb_user_unique_id || claims?.sub || null;
  }, [accessToken]);

  const [currentRoleTitle, setCurrentRoleTitle] = useState(null);
  const [currentRoleLoading, setCurrentRoleLoading] = useState(true);
  // The current role's function (jdMetadataJson.function) rides along to the
  // pathways page in the onSaved values — it labels and drives the
  // department filter checkbox there. Seeded from the parent's values so
  // re-opening the form via Edit doesn't lose it if the re-fetch fails.
  const [currentFunction, setCurrentFunction] = useState(initialAspiration?.currentFunction ?? null);

  useEffect(() => {
    if (!currentUserId) {
      setCurrentRoleLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const profile = await getTalentMobilityPeopleProfileDetails({ user_id: currentUserId });
        if (!cancelled) {
          setCurrentRoleTitle(profile?.currentRole?.roleName || null);
          setCurrentFunction(profile?.currentRole?.jdMetadataJson?.function || null);
        }
      } catch {
        // apiService already logs; the line just falls back to "—"
      } finally {
        if (!cancelled) setCurrentRoleLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUserId]);

  const locationOptions = useMemo(() => getOrgLocationNames(orgMetaData), [orgMetaData]);

  // Membership, not list length: a hydrated selection comes from the
  // backend's saved list, which can carry locations since removed from org
  // metadata — equal lengths would then fake a checked "All Locations".
  const allSelected =
    locationOptions.length > 0 && locationOptions.every((loc) => locations.includes(loc));

  const handleAllToggle = (checked) => setLocations(checked ? locationOptions : []);

  const handleMobileChange = (value) => {
    setMobile(value);
    if (!value) setLocations([]);
  };

  // Business Unit/Function/Sub-function are optional — they only narrow the
  // Target Role dropdown, so Save is gated on Target Role/relocation alone.
  const canSave = targetRoleId != null && mobile != null && (!mobile || locations.length > 0);

  // Hands the captured aspiration to the parent (TalentMobilityEmployeeV2),
  // which fires POST /career-paths and switches back to the pathways view
  // immediately — this form itself never touches the API or pathway state.
  // The org placement fields ride along client-side only; the parent's
  // /career-paths payload doesn't send them.
  const handleSave = () => {
    onSaved?.({
      ...placementCascade.placement,
      targetRoleId,
      targetRoleTitle: roleOptions.find((option) => option.value === targetRoleId)?.label || null,
      mobile,
      locations: mobile ? locations : [],
      currentFunction,
    });
  };

  return (
    <PageTransitionStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <HeadingRowStyled>
            <Title level={4}>Tell us where you want to go</Title>
          </HeadingRowStyled>
          <Text type="secondary">This drives the career pathways and open-role recommendations below.</Text>
        </HeaderLeftStyled>
      </PageHeaderStyled>

      <CardStyled>
        <CurrentRoleRowStyled>
          Current Role:{" "}
          {currentRoleLoading ? <SbSpin size="small" /> : <strong>{currentRoleTitle || "—"}</strong>}
        </CurrentRoleRowStyled>

        <Row gutter={16}>
          <Col span={12}>
            <FieldBlockStyled>
              <Title level={4} style={{ margin: 0 }}>
                Set Career Target:
              </Title>
            </FieldBlockStyled>
          </Col>
          {ORG_PLACEMENT_FIELDS.map(({ name, label, parent }) => (
            <Col span={12} key={name}>
              <FieldBlockStyled>
                <Text strong>{label}</Text>
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={placementCascade.options[name]}
                  value={placementCascade.placement[name]}
                  disabled={Boolean(parent) && placementCascade.placement[parent] == null}
                  onChange={(value) => placementCascade.setField(name, value)}
                  placeholder={`Select ${label}`}
                />
              </FieldBlockStyled>
            </Col>
          ))}
        </Row>

        <FieldBlockStyled>
          <RequiredLabel>Target Role</RequiredLabel>
          <Select
            showSearch
            optionFilterProp="label"
            options={roleOptions}
            loading={publishedJobs === null}
            notFoundContent={publishedJobs === null ? <SbSpin size="small" /> : undefined}
            value={targetRoleId}
            onChange={setTargetRoleId}
            placeholder={publishedJobs === null ? "Loading target roles..." : "Search or select a target role..."}
          />
        </FieldBlockStyled>

        <FieldBlockStyled>
          <RequiredLabel>Open to relocation?</RequiredLabel>
          <RadioRowStyled>
            <Radio.Group value={mobile} onChange={(e) => handleMobileChange(e.target.value)}>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </RadioRowStyled>
        </FieldBlockStyled>

        {mobile && (
          <FieldBlockStyled>
            <Text strong>Preferred Locations</Text>
            {locationOptions.length === 0 ? (
              <Text type="secondary">No org locations configured yet.</Text>
            ) : (
              <>
                <Checkbox checked={allSelected} onChange={(e) => handleAllToggle(e.target.checked)}>
                  All Locations
                </Checkbox>
                <SbSelect
                  mode="multiple"
                  allowClear
                  placeholder="Select one or more locations..."
                  value={locations}
                  options={locationOptions.map((loc) => ({ value: loc, label: loc }))}
                  onChange={setLocations}
                />
              </>
            )}
          </FieldBlockStyled>
        )}

        <SbButton buttonType="primary" disabled={!canSave} onClick={handleSave}>
          Save &amp; Continue
        </SbButton>
      </CardStyled>
    </PageTransitionStyled>
  );
};

export default AspirationCaptureV2;
